import { DIMENSION_TITLES, MEASURE_TITLES } from '../analytics'
import { MEASURE_DEFINITION_BY_TITLE } from '../analyticsDefinitions'
import { EXPLORER_TYPE } from '../renderTypes'
import { getTableViewsFilters } from '~/services/explore'
import { FilterOperator, OrderValue } from '~/types/cube'
import { SourceType } from '~/types/sources'
import { DashboardModuleType, type DashboardConfig } from '~/types/dashboards'

export const createAbsenteeismDashboardConfig = (): DashboardConfig => {
  return [
    {
      titleKey: 'dashboard.absenteeism.absenceMetrics',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.absenteeism.absentDays.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_SUM_ABSENCE_AMOUNT_UNPLANNED,
                type: EXPLORER_TYPE.LIST
              },
              infoContent: {
                definition: 'dashboard.absenteeism.absentDays.info.definition',
                source: SourceType.HRIS,
                insight: 'dashboard.absenteeism.absentDays.info.insight'
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_SUM_ABSENCE_AMOUNT_UNPLANNED]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.absenteeism.absenteeismRate.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_ABSENTEEISM_RATE,
                type: EXPLORER_TYPE.KPI
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_ABSENTEEISM_RATE
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_ABSENTEEISM_RATE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_ABSENTEEISM_RATE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.DISTRIBUTION,
          options: {
            config: {
              titleKey:
                'dashboard.absenteeism.unplannedAbsentDaysByCategory.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_SUM_ABSENCE_AMOUNT_UNPLANNED,
                dimension: DIMENSION_TITLES.EMPLOYEE_ABSENCE_CATEGORY,
                type: EXPLORER_TYPE.DOUGHNUT
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_SUM_ABSENCE_AMOUNT_UNPLANNED],
              dimensions: [DIMENSION_TITLES.EMPLOYEE_ABSENCE_CATEGORY],
              filters: [
                {
                  member: DIMENSION_TITLES.EMPLOYEE_ABSENCE_CATEGORY,
                  operator: FilterOperator.SET,
                  values: []
                }
              ],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.absenteeism.ratioEmployeeAbsent.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_RATIO_EMPLOYEE_ABSENT,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_RATIO_EMPLOYEE_ABSENT
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_RATIO_EMPLOYEE_ABSENT]
            }
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.absenteeism.mostAbsentEmployees.title',
              columnOrder: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                MEASURE_TITLES.EMPLOYEE_SUM_ABSENCE_AMOUNT_UNPLANNED,
                DIMENSION_TITLES.EMPLOYEE_ARRIVAL_DATE
              ],
              columnsTranslationPrefix:
                'dashboard.absenteeism.mostAbsentEmployees.columns',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_SUM_ABSENCE_AMOUNT_UNPLANNED,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_SUM_ABSENCE_AMOUNT_UNPLANNED],
              dimensions: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_ARRIVAL_DATE
              ],
              order: [
                [
                  MEASURE_TITLES.EMPLOYEE_SUM_ABSENCE_AMOUNT_UNPLANNED,
                  OrderValue.DESC
                ]
              ],
              filters: getTableViewsFilters(
                MEASURE_TITLES.EMPLOYEE_SUM_ABSENCE_AMOUNT_UNPLANNED
              ),
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.absenteeism.absenceFrequency.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_ABSENCE_FREQUENCY,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_ABSENCE_FREQUENCY
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_ABSENCE_FREQUENCY]
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.absenteeism.durationMetrics',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey:
                'dashboard.absenteeism.avgAbsenceUnplannedDuration.title',
              exploreLink: {
                measure:
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_ABSENCE_UNPLANNED_DURATION,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_ABSENCE_UNPLANNED_DURATION
                ]
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_AVERAGE_ABSENCE_UNPLANNED_DURATION
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.DISTRIBUTION,
          options: {
            config: {
              titleKey:
                'dashboard.absenteeism.countAbsenceUnplannedByAbsenceDurationRange.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_ABSENCE_UNPLANNED,
                dimension: DIMENSION_TITLES.EMPLOYEE_ABSENCE_DURATION_RANGE,
                type: EXPLORER_TYPE.DOUGHNUT
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_ABSENCE_UNPLANNED],
              dimensions: [DIMENSION_TITLES.EMPLOYEE_ABSENCE_DURATION_RANGE],
              filters: [
                {
                  member: DIMENSION_TITLES.EMPLOYEE_ABSENCE_DURATION_RANGE,
                  operator: FilterOperator.SET,
                  values: []
                }
              ],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey:
                'dashboard.absenteeism.ratioAbsenceUnplannedShort.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_RATIO_UNPLANNED_SHORT_ABSENCE,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_RATIO_UNPLANNED_SHORT_ABSENCE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_RATIO_UNPLANNED_SHORT_ABSENCE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.absenteeism.absenteeismCostRate.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_ABSENTEEISM_COST_RATE,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_ABSENTEEISM_COST_RATE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_ABSENTEEISM_COST_RATE]
            }
          }
        }
      ]
    }
  ]
}
